import { TaskListUnit } from '../../../API';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../../graphql/conflictResolution/types';
import {mergeTaskListsByCurrentState, normalizeTaskListUnitStructure} from '../taskListUnitStructure';

/**
 * Conflict handler for TaskListUnit entities.
 * Handles version conflicts during task list unit updates with intelligent merging.
 */
export const taskListUnitConflictHandler: ConflictHandler<TaskListUnit> = {
    entityType: 'TaskListUnit',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<TaskListUnit> {
        throw new Error(
            'TaskListUnit refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<TaskListUnit>): Partial<TaskListUnit> {
        const merged: Partial<TaskListUnit> = {
            version: currentState.version,
            id: currentState.id
        };

        if (intendedChanges.name !== undefined) {
            merged.name = intendedChanges.name;
        }
        if (intendedChanges.description !== undefined) {
            merged.description = intendedChanges.description;
        }
        if (intendedChanges.avatarPath !== undefined) {
            merged.avatarPath = intendedChanges.avatarPath;
        }
        if (intendedChanges.timezone !== undefined) {
            merged.timezone = intendedChanges.timezone;
        }
        if (intendedChanges.logicalDayShift !== undefined) {
            merged.logicalDayShift = intendedChanges.logicalDayShift;
        }
        if (intendedChanges.isArchived !== undefined) {
            merged.isArchived = intendedChanges.isArchived;
        }

        if (intendedChanges.taskLists !== undefined || intendedChanges.taskListsOrder !== undefined) {
            const mergedTaskLists = intendedChanges.taskLists !== undefined
                ? mergeTaskListsByCurrentState(currentState.taskLists, intendedChanges.taskLists)
                : mergeTaskListsByCurrentState(currentState.taskLists, currentState.taskLists);

            const mergedTaskListsOrder = intendedChanges.taskListsOrder !== undefined
                ? mergeTaskListOrder(currentState.taskListsOrder ?? [], intendedChanges.taskListsOrder)
                : currentState.taskListsOrder ?? [];

            const normalized = normalizeTaskListUnitStructure(mergedTaskLists, mergedTaskListsOrder);
            merged.taskLists = normalized.taskLists;
            merged.taskListsOrder = normalized.taskListsOrder;
        }

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        return attemptNumber < 3;
    }
};

function mergeTaskListOrder(
    currentOrder: string[],
    intendedOrder: string[]
): string[] {
    const addedItems = currentOrder.filter(id => !intendedOrder.includes(id));
    const validItems = intendedOrder.filter(id => currentOrder.includes(id));
    return [...validItems, ...addedItems];
}
